'use client';

import { Image } from '@/components/ui/image/image';
import { Link } from '@/components/ui/link/link';
import LottieComponent from '@/components/ui/card-with-image/lottie-component';
import { assetIdFromUrl, useLogoAlt } from '@/components/studio/LogoAltContext';
import { cn } from '@/lib/utils';
import { parseMarkdownLinks } from '@/lib/utils/parse-markdown-links';
import { ChevronDownIcon } from 'lucide-react';
import { HTMLAttributes, useContext, useEffect, useId, useRef, useState } from 'react';
import styles from '../../ui/accordion-feat/accordion-feat.module.scss';
import { TabGroupContext } from './tabGroupExperience';

function normalizeUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.startsWith('//') ? `https:${url}` : url;
}

// Only strips width/height from the root <svg> element, not from nested elements
// (e.g. <rect> inside <clipPath>), to avoid breaking SVG masks/clips.
const InlineSvgClient = ({ src }: { src: string }) => {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    fetch(src)
      .then((res) => (res.ok ? res.text() : null))
      .then((text) => {
        if (!text?.includes('<svg')) return;
        // Strip fills/strokes outside of <mask>, <clipPath>, <defs> so
        // luminance masks (fill="white") are preserved.
        const maskRe = /<(mask|clipPath|defs)\b[\s\S]*?<\/\1>/gi;
        const preserved: string[] = [];
        const stripped = text.replace(maskRe, (m) => {
          preserved.push(m);
          return `__PRESERVED_${preserved.length - 1}__`;
        });
        const processed = stripped
          .replace(/<svg([^>]*)>/, (_, attrs: string) => {
            const cleaned = attrs.replace(/\s*width="[^"]*"/, '').replace(/\s*height="[^"]*"/, '');
            return `<svg${cleaned} width="42" height="42">`;
          })
          .replace(/fill="(?!none)[^"]*"/g, 'fill="currentColor"')
          .replace(/stroke="(?!none)[^"]*"/g, 'stroke="currentColor"')
          .replace(/__PRESERVED_(\d+)__/g, (_, i) => preserved[Number(i)]);
        setSvg(processed);
      })
      .catch(() => null);
  }, [src]);

  if (!svg) return null;
  return <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />;
};

export type TabSectionItemExperienceProps = {
  title?: string;
  description?: string;
  image?: string | null;
  imageAspectRatio?: '1:1' | '4:5' | '16:9';
  imageFit?: 'contain' | 'cover';
  icon?: string | null;
  ctaLabel?: string;
  ctaUrl?: string;
};

export const TabSectionItemExperience = ({
  title = '',
  description = '',
  image,
  imageAspectRatio,
  imageFit,
  icon,
  ctaLabel,
  ctaUrl,
  children,
  className,
  ...props
}: TabSectionItemExperienceProps & HTMLAttributes<HTMLDivElement>) => {
  const context = useContext(TabGroupContext);
  const logoAltMap = useLogoAlt();
  const id = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const imageUrl = normalizeUrl(image ?? undefined);
  const iconUrl = normalizeUrl(icon ?? undefined);
  const isSvg = iconUrl?.toLowerCase().includes('.svg') ?? false;

  // Alt text comes from the bound image asset's description (resolved server-side and provided via
  // LogoAltContext), keyed by the asset id parsed from the image URL. No tab-title fallback.
  const assetId = imageUrl ? assetIdFromUrl(imageUrl) : undefined;
  const alt = (assetId && logoAltMap[assetId]) || '';
  const imageData = imageUrl
    ? { url: imageUrl, alt, aspectRatio: imageAspectRatio, fit: imageFit }
    : undefined;

  // Register on mount so the parent group context knows this item exists.
  // Unregister on unmount so the group can recover if Studio remounts this
  // component when an asset is bound (new instance = new id).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    context?.registerItem(id, imageData);
    return () => context?.unregisterItem(id);
  }, []);

  // When the image asset is bound/changed in Studio, push the new URL to
  // the group context so the preview updates without needing a click.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    () => context?.updateItemImage(id, imageData),
    [imageUrl, alt, imageAspectRatio, imageFit],
  );

  useEffect(() => {
    if (contentRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      });
      resizeObserver.observe(contentRef.current);
      setContentHeight(contentRef.current.scrollHeight);
      return () => resizeObserver.disconnect();
    }
  }, [description]);

  if (!context) {
    return (
      <div className={className} {...props}>
        <p>Place inside a Tab Group component.</p>
      </div>
    );
  }

  const { activeItemId, activateItem } = context;
  const isOpen = activeItemId === id;

  return (
    <>
      <div
        className={cn(
          styles.component_item,
          { [styles['component_item--expanded']]: isOpen },
          className,
        )}
        {...props}
      >
        <button
          id={`tab-section-trigger-${id}`}
          onClick={() => activateItem(id, imageData)}
          aria-expanded={isOpen}
          aria-controls={`tab-section-content-${id}`}
          className={styles.component_trigger}
        >
          {iconUrl && (
            <span className={styles.component_icon}>
              {isSvg ? (
                <InlineSvgClient src={iconUrl} />
              ) : (
                <Image src={iconUrl} alt="" aria-hidden="true" width={42} height={42} />
              )}
            </span>
          )}
          <span className={styles.component_title}>{title || 'Tab item'}</span>
          <span
            className={cn(styles.component_chevron, {
              [styles['component_chevron--open']]: isOpen,
            })}
            aria-hidden="true"
          >
            <ChevronDownIcon />
          </span>
        </button>
        <div
          id={`tab-section-content-${id}`}
          className={cn(styles.component_content, {
            [styles['component_content--open']]: isOpen,
          })}
          role="region"
          aria-labelledby={`tab-section-trigger-${id}`}
          style={{ maxHeight: isOpen ? `${contentHeight}px` : '0px' }}
        >
          <div
            ref={contentRef}
            className={cn(styles.component_content_inner, '!text-left', {
              [styles['component_content_inner--open']]: isOpen,
            })}
          >
            <p>{parseMarkdownLinks(description, styles.component_link)}</p>
            {/* Legacy text-field CTA — kept for content authored before the Link component existed. */}
            {ctaLabel && ctaUrl && (
              <div className={styles.component_cta}>
                <Link label={ctaLabel} url={ctaUrl} type="Button" buttonStyle="Pink" withArrow />
              </div>
            )}
            {/* Drop-zone for child components dragged into the tab content (e.g. the Link component). */}
            {children && <div className={styles.component_cta}>{children}</div>}
            {imageUrl && (
              <div className={styles.component_image}>
                {/\.lottie(\?|$)/i.test(imageUrl) ? (
                  <LottieComponent src={imageUrl} className="h-full w-full" />
                ) : (
                  <Image
                    src={imageUrl}
                    alt={alt}
                    width={0}
                    height={0}
                    sizes="100%"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      objectFit: imageFit ?? 'cover',
                      objectPosition: 'center',
                      aspectRatio: imageAspectRatio
                        ? imageAspectRatio.replace(':', ' / ')
                        : undefined,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.component_separator} aria-hidden="true" />
    </>
  );
};
